import React, {createContext, useContext, useState, ReactNode, useCallback, useEffect} from 'react';
import {usePriceQuoteDetailsContext} from "./PriceQuoteDetailsContext";
import {useHistory} from "react-router-dom";
import {isEqual, isFunction} from "lodash";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../../redux/store";
import {getPriceCatalogItems, setPriceCatalogForTempShuma} from '../_redux/actions/popupPriceCatalogActions';
import {optionsVatType} from "../../CustomerDetails/constants/options";
import {useTempInspectionsListContext} from "./TempInspectionsListContext";

interface QueryParams {
  [key: string]: any;
}

interface ContextType {
  queryParams: QueryParams;
  setQueryParams: (
    nextQueryParams: QueryParams | ((prev: QueryParams) => QueryParams)
  ) => void;
  setQueryParamsBase: React.Dispatch<React.SetStateAction<QueryParams>>;
  handleGetCatalogData: (servicePrice: number, serviceDescription: string) => void;
  show: boolean;
  handleShowModal: () => void;
  handleCloseModal: () => void;
  showIcon: boolean | null;
}

const defaultContext: ContextType = {
  queryParams: {},
  setQueryParams: () => {
  },
  setQueryParamsBase: () => {
  },
  handleGetCatalogData: () => {
  },
  show: false,
  handleShowModal: () => {
  },
  handleCloseModal: () => {
  },
  showIcon: false
};

const PopupPriceCatalogContext = createContext<ContextType>(defaultContext);

export function usePopupPriceCatalogContext() {
  return useContext(PopupPriceCatalogContext);
}

export function PopupPriceCatalogProvider({children, index, tempShumaID}: {index: number, tempShumaID?: string, children: ReactNode}) {
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const handleShowModal = () => setShow(true);
  const handleCloseModal = () => setShow(false);
  const {formik} = usePriceQuoteDetailsContext();
  const {getInspections} = useTempInspectionsListContext();
  const history = useHistory();
  const [queryParams, setQueryParamsBase] = useState({});
  const setQueryParams = useCallback((nextQueryParams: any) => {
    setQueryParamsBase(prevQueryParams => {
      if (isFunction(nextQueryParams)) {
        nextQueryParams = nextQueryParams(prevQueryParams);
      }

      if (isEqual(prevQueryParams, nextQueryParams)) {
        return prevQueryParams;
      }
      for (let key in nextQueryParams) {
        if (nextQueryParams.hasOwnProperty(key) && !nextQueryParams[key]) delete nextQueryParams[key]
      }
      const path = history.location.pathname;
      const newQueryParams = new URLSearchParams(nextQueryParams);
      history.replace(`${path}?${newQueryParams.toString()}`);

      return nextQueryParams;
    });
  }, []);

  useEffect(() => {
    dispatch(getPriceCatalogItems(queryParams));
  }, []);

  const {priceCatalogList} = useSelector(
    (state: RootState) => ({
      // @ts-ignore
      priceCatalogList: state.popupPriceCatalogTableReducer.entities,
    }), shallowEqual
  );

  const handleGetCatalogData = (
    servicePrice: number,
    serviceDescription: string
  ) => {
    const lineIndex = formik.values.lines[index]
      ? index
      : formik.values.lines.length;

    if (tempShumaID) setPriceCatalogForTempShuma(tempShumaID);

    formik.setFieldValue(
      `lines.${lineIndex}`,
      {
        ...formik.values.lines[lineIndex],
        price: servicePrice,
        description: serviceDescription,
        quantity: formik.values.lines[lineIndex]?.quantity || 1,
        vatType: optionsVatType[1],
      },
      false
    );

    getInspections();

    handleCloseModal();
    setQueryParams({});
  };

  return (
    <PopupPriceCatalogContext.Provider value={{
      ...defaultContext,
      queryParams,
      setQueryParamsBase,
      setQueryParams,
      handleGetCatalogData,
      show,
      handleShowModal,
      handleCloseModal,
      showIcon: priceCatalogList && priceCatalogList.length > 0
    }}>
      {children}
    </PopupPriceCatalogContext.Provider>
  );
}